/**
 * /ai/skills/:skillName — skill editor.
 *
 * Tabs: Prompt (text editor + version history + tuning log + Run-eval nudge
 * after save), Schema / Template / Enums / Config (validated JSON editors —
 * every save snapshots a version via docaiSkills.update), Validators
 * (READ-ONLY code view — edited via Cursor + `docai skills push`), Goldens
 * (markdown viewer), and a version history list.
 *
 * Draft skills additionally offer "Discard draft".
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Badge,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Code,
  Select,
  Alert,
  AlertIcon,
  Input,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiSave, FiPlay, FiTrash2 } from 'react-icons/fi';
import { callDocai, useRunPoller } from '../../hooks/useDocAI';
import JsonEditor from '../../components/AI/JsonEditor';
import RunProgressBar from '../../components/AI/RunProgressBar';

const JSON_TABS = ['schema', 'template', 'enums', 'config'];

export default function AISkillDetailPage() {
  const { skillName } = useParams();
  const history = useHistory();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [jsonDrafts, setJsonDrafts] = useState({}); // { schema: obj|undefined, ... }
  const [changeNote, setChangeNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [evalRunId, setEvalRunId] = useState(null);
  const [goldenIdx, setGoldenIdx] = useState(0);
  const { run: evalRun } = useRunPoller(evalRunId);

  const codeBg = useColorModeValue('gray.50', 'gray.900');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiSkills', { action: 'get', name: skillName });
      setData(res);
      setPrompt(res.skill?.prompt || '');
      setJsonDrafts({});
      setChangeNote('');
    } catch (err) {
      toast({ title: 'Skill load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [skillName, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (evalRun && ['done', 'error', 'cancelled'].includes(evalRun.status)) {
      if (evalRun.status === 'done') {
        const t = evalRun.totals || {};
        toast({
          title: `Eval complete — ${t.perfectDocs}/${t.docs} perfect, ${((t.fieldAccuracy || 0) * 100).toFixed(2)}% fields`,
          status: t.perfectDocs === t.docs ? 'success' : 'warning',
          duration: 8000,
        });
      } else if (evalRun.status === 'error') {
        toast({ title: 'Eval failed', description: evalRun.error, status: 'error', duration: 8000 });
      }
      setEvalRunId(null);
      load();
    }
  }, [evalRun, toast, load]);

  const skill = data?.skill;

  const save = async (patch) => {
    setSaving(true);
    try {
      await callDocai('docaiSkills', {
        action: 'update',
        name: skillName,
        patch,
        changeNote: changeNote || null,
      });
      toast({
        title: 'Saved — version snapshotted',
        description: 'Run an eval to prove the change against the goldens.',
        status: 'success',
        duration: 5000,
      });
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const runEval = async () => {
    try {
      const res = await callDocai('docaiRuns', { action: 'start', kind: 'eval', skill: skillName, params: {} });
      setEvalRunId(res.runId);
      toast({ title: 'Eval queued', status: 'info', duration: 2500 });
    } catch (err) {
      toast({ title: 'Eval start failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const discardDraft = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Discard draft skill "${skillName}"? This deletes the skill row (documents/extractions stay).`)) return;
    try {
      await callDocai('docaiSkills', { action: 'discardDraft', name: skillName });
      toast({ title: 'Draft discarded', status: 'success', duration: 3000 });
      history.push('/ai/skills');
    } catch (err) {
      toast({ title: 'Discard failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  if (loading && !data) {
    return <Center h="60vh"><Spinner size="xl" color="blue.400" /></Center>;
  }
  if (!skill) {
    return (
      <Center h="60vh" flexDirection="column">
        <Text mb={3}>Skill not found.</Text>
        <Button as={Link} to="/ai/skills" leftIcon={<FiArrowLeft />}>All skills</Button>
      </Center>
    );
  }

  const promptDirty = prompt !== (skill.prompt || '');
  const goldens = data.goldens || [];

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap">
        <HStack>
          <Button as={Link} to="/ai/skills" size="sm" variant="ghost" leftIcon={<FiArrowLeft />} />
          <Heading size="md" fontFamily="mono">{skill.name}</Heading>
          {skill.is_draft && <Badge colorScheme="orange">draft</Badge>}
          <Badge variant="outline">v{skill.version}</Badge>
        </HStack>
        <HStack>
          {skill.is_draft && (
            <Button size="sm" colorScheme="red" variant="outline" leftIcon={<FiTrash2 />} onClick={discardDraft}>
              Discard draft
            </Button>
          )}
          <Button size="sm" leftIcon={<FiPlay />} colorScheme="green" onClick={runEval} isDisabled={!!evalRunId}>
            Run eval
          </Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={load} isLoading={loading} />
        </HStack>
      </HStack>

      {evalRun && (
        <Box mb={4}><RunProgressBar run={evalRun} /></Box>
      )}

      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Tag size="sm">{skill.config?.model || 'no model'}</Tag>
        <Tag size="sm">verifier: {skill.config?.verifier?.model || 'default'}</Tag>
        <Tag size="sm" colorScheme="purple">{goldens.length} goldens</Tag>
        <Tag size="sm">{skill.doc_type || '—'}{skill.lender ? ` · ${skill.lender}` : ''}</Tag>
      </HStack>

      <Input
        size="sm"
        mb={4}
        maxW="560px"
        placeholder="Change note for the next save (optional, lands in version history)"
        value={changeNote}
        onChange={(e) => setChangeNote(e.target.value)}
      />

      <Tabs size="sm" colorScheme="blue" isLazy>
        <TabList flexWrap="wrap">
          <Tab>Prompt{promptDirty ? ' •' : ''}</Tab>
          {JSON_TABS.map((t) => (
            <Tab key={t} textTransform="capitalize">
              {t}{jsonDrafts[t] !== undefined ? ' •' : ''}
            </Tab>
          ))}
          <Tab>Validators</Tab>
          <Tab>Goldens ({goldens.length})</Tab>
          <Tab>Versions ({(data.versions || []).length})</Tab>
          <Tab>Tuning log</Tab>
        </TabList>
        <TabPanels>
          {/* Prompt */}
          <TabPanel px={0}>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fontFamily="mono"
              fontSize="xs"
              minH="480px"
              bg={codeBg}
              spellCheck={false}
            />
            <HStack mt={3}>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiSave />}
                isDisabled={!promptDirty}
                isLoading={saving}
                onClick={() => save({ prompt })}
              >
                Save prompt (v{skill.version} → v{skill.version + 1})
              </Button>
              {promptDirty && (
                <Button size="sm" variant="ghost" onClick={() => setPrompt(skill.prompt || '')}>
                  Revert
                </Button>
              )}
            </HStack>
            <Alert status="info" mt={3} borderRadius="md" fontSize="xs">
              <AlertIcon />
              Saving changes the prompt hash: cached extractions become stale and the next eval/fleet re-extracts
              fresh. Always Run eval after saving — the tuner's regression gate uses the same goldens.
            </Alert>
          </TabPanel>

          {/* JSON tabs */}
          {JSON_TABS.map((t) => (
            <TabPanel key={t} px={0}>
              <JsonEditor
                value={skill[t] ?? {}}
                onChange={(parsed) => setJsonDrafts((prev) => ({ ...prev, [t]: parsed }))}
              />
              <HStack mt={3}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<FiSave />}
                  isDisabled={jsonDrafts[t] === undefined}
                  isLoading={saving}
                  onClick={() => save({ [t]: jsonDrafts[t] })}
                >
                  Save {t}
                </Button>
              </HStack>
              {t === 'schema' && (
                <Alert status="warning" mt={3} borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Schema participates in the prompt hash — saving invalidates extraction caches (same as prompt).
                </Alert>
              )}
            </TabPanel>
          ))}

          {/* Validators (read-only) */}
          <TabPanel px={0}>
            <Alert status="info" mb={3} borderRadius="md" fontSize="xs">
              <AlertIcon />
              Validators are deterministic JS and deliberately NOT editable here. Edit
              {' '}<Code fontSize="xs">docai/skills/{skill.name}/validators.js</Code> in Cursor, then run
              {' '}<Code fontSize="xs">docai skills push {skill.name}</Code>.
            </Alert>
            <Code as="pre" display="block" w="100%" p={3} fontSize="xs" whiteSpace="pre-wrap" borderRadius="md" bg={codeBg} maxH="520px" overflowY="auto">
              {skill.validators || '// no validators'}
            </Code>
          </TabPanel>

          {/* Goldens */}
          <TabPanel px={0}>
            {goldens.length ? (
              <>
                <Select size="sm" maxW="480px" mb={3} value={goldenIdx} onChange={(e) => setGoldenIdx(Number(e.target.value))}>
                  {goldens.map((g, i) => (
                    <option key={g.id} value={i}>{g.file_name}</option>
                  ))}
                </Select>
                <HStack mb={2} spacing={3}>
                  <Tag size="sm">{goldens[goldenIdx]?.source}</Tag>
                  <Text fontSize="xs" color="gray.500">
                    promoted by {goldens[goldenIdx]?.promoted_by || '—'} · {new Date(goldens[goldenIdx]?.created_at).toLocaleString()}
                  </Text>
                  {goldens[goldenIdx]?.document_id && (
                    <Button
                      as={Link}
                      to={`/ai/documents/${goldens[goldenIdx].document_id}`}
                      size="xs"
                      variant="outline"
                    >
                      Open document
                    </Button>
                  )}
                </HStack>
                <Code as="pre" display="block" w="100%" p={3} fontSize="xs" whiteSpace="pre-wrap" borderRadius="md" bg={codeBg} maxH="520px" overflowY="auto">
                  {goldens[goldenIdx]?.markdown}
                </Code>
              </>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No goldens yet — promote verified documents from the review workspace.
              </Text>
            )}
          </TabPanel>

          {/* Versions */}
          <TabPanel px={0}>
            <Table size="sm">
              <Thead>
                <Tr><Th>Version</Th><Th>Source</Th><Th>Note</Th><Th>By</Th><Th>When</Th></Tr>
              </Thead>
              <Tbody>
                {(data.versions || []).map((v) => (
                  <Tr key={v.id}>
                    <Td fontSize="xs">v{v.version}</Td>
                    <Td><Tag size="sm">{v.change_source}</Tag></Td>
                    <Td fontSize="xs" maxW="380px"><Text noOfLines={2}>{v.change_note || '—'}</Text></Td>
                    <Td fontSize="xs">{v.created_by || '—'}</Td>
                    <Td fontSize="xs">{new Date(v.created_at).toLocaleString()}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TabPanel>

          {/* Tuning log */}
          <TabPanel px={0}>
            {data.tuningLog ? (
              <Code as="pre" display="block" w="100%" p={3} fontSize="xs" whiteSpace="pre-wrap" borderRadius="md" bg={codeBg}>
                {data.tuningLog}
              </Code>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No tuning log yet — run a tune from the Improvement page once corrections accumulate.
              </Text>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
